import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";

import { showError } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import { Select } from "components/select/";
import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import TextField from "components/form/textField/TextField";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import {
  GetReservationModuleAll,
  PostReservationNotification,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import { IModule } from "redux/reducers/models/reservations";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";
import { formatDate } from "helpers/Helpers";

interface IPostReservationNotification {
  module_id: number;
  start_date: string;
  end_date: string;
  user_type: "admin" | "customer";
  subject: string;
  content: string;
  type: "forced" | "passive";
  date_range: string[];
  dateRangeStart: Date;
  dateRangeEnd: Date;
}

export default function TeeSheetNotificationNew() {
  const history = useHistory();
  const { t } = useTranslation();
  const windowSize = useWindowSize();

  const dispatch = useAppDispatch();

  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:00"];
  }

  const today = new Date();

  const [notificationState, setNotificationState] = useState<IPostReservationNotification>({
    module_id: null,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    user_type: undefined,
    subject: "",
    content: "",
    type: undefined,
    date_range: formatDateRange(today, today),
    dateRangeStart: today,
    dateRangeEnd: today,
  });

  const [modules, setModules] = useState<IModule[]>(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAllReservationModules(source.token);
    return () => source.cancel();
  }, []);

  async function postNotification() {
    //saves an API call if required fields caught before
    if (notificationState.module_id === null) {
      dispatch(showError(t("Activity type required")));
      return;
    }

    if (notificationState.start_date === "") {
      dispatch(showError(t("Notification Start Date Required")));
      return;
    }

    if (notificationState.end_date === "") {
      dispatch(showError(t("Notification End Date Required")));
      return;
    }

    if (notificationState.user_type === undefined) {
      dispatch(showError(t("User Type Required")));
      return;
    }

    if (notificationState.type === undefined) {
      dispatch(showError(t("Type Required")));
      return;
    }
    if (notificationState.subject === "") {
      dispatch(showError(t("Subject Required")));
      return;
    }
    if (notificationState.content === "") {
      dispatch(showError(t("Content Required")));
      return;
    }

    const postNotificationRes = await PostReservationNotification(
      {
        start_date: notificationState.start_date,
        end_date: notificationState.end_date,
        subject: notificationState.subject,
        user_type: notificationState.user_type,
        type: notificationState.type,
        content: notificationState.content,
        module_id: notificationState.module_id,
      },
      true,
    );

    if (postNotificationRes.status !== StatusCode.OK) {
      dispatch(showError(typeof postNotificationRes.data === "string" ? postNotificationRes.data : t("Error creating notification")));
      return;
    }

    history.push("/admin/settings/reservations/notifications");
  }

  async function loadAllReservationModules(token?: CancelToken) {
    if (modules !== undefined) {
      setModules(undefined);
    }
    const { status, data } = await GetReservationModuleAll(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (status !== StatusCode.OK) {
      dispatch(showError(t("Error loading modules")));
    }

    setModules(status !== StatusCode.OK ? [] : data);
  }

  function handleDropDownChange(value: any, property: string) {
    setNotificationState({
      ...notificationState,
      [property]: value,
    });
  }

  const primaryAction = {
    content: t("Create"),
    action: () => postNotification(),
  };

  useEffect(() => {
    if (notificationState.dateRangeStart && notificationState.dateRangeEnd) {
      const date_range = formatDateRange(notificationState.dateRangeStart, notificationState.dateRangeEnd);
      setNotificationState(prevState => ({ ...prevState, date_range }));
    }
  }, [notificationState.dateRangeStart, notificationState.dateRangeEnd]);

  function setDateRangeStart(date: Date) {
    const startDateAsString = new Date(date).toISOString().slice(0, 10);

    setNotificationState(prevState => ({
      ...prevState,
      dateRangeStart: date,
      start_date: startDateAsString,
    }));
  }

  function setDateRangeEnd(date: Date) {
    const dateAsString = new Date(date).toISOString().slice(0, 10);

    setNotificationState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
      end_date: dateAsString,
    }));
  }

  return (
    <Page
      title={t("New Activity Notification")}
      narrow
      primaryAction={primaryAction}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Notifications"),
          url: "/admin/settings/reservations/notifications",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <div id="notification-date-selector" style={{ marginRight: "15px" }}>
                <DatePickerInput
                  label={t("Notification Date")}
                  months={windowSize.width > TABLET_WIDTH ? 2 : 1}
                  position="left"
                  showQuickOptions={windowSize.width > MOBILE_WIDTH}
                  startingDate={notificationState.dateRangeStart}
                  setStartingDate={setDateRangeStart}
                  endingDate={notificationState.dateRangeEnd}
                  setEndingDate={setDateRangeEnd}
                />
              </div>
              <div>
                <Select
                  label={t("Activity Type")}
                  onChange={(value: any) => handleDropDownChange(value, "module_id")}
                >
                  {modules?.map((module: any, index: number) => {
                    return (
                      <Select.Option key={index} value={module.id} name={module.title}>
                        {module.title}
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>
            </FormLayout.Group>

            <FormLayout.Group>
              <Select
                label={t("User Type")}
                onChange={(value: any) => handleDropDownChange(value, "user_type")}
              >
                {notificationUserTypes(t).map((type, index: number) => {
                  return (
                    <Select.Option key={index} value={type.normalized} name={type.title}>
                      {type.title}
                    </Select.Option>
                  );
                })}
              </Select>

              <Select
                label={t("Type")}
                onChange={(value: any) => handleDropDownChange(value, "type")}
              >
                <Select.Option name={"forced"} value={"forced"}>
                  {t("Forced")}
                </Select.Option>{" "}
                <Select.Option value={"passive"} name={"passive"}>
                  {t("Passive")}
                </Select.Option>
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={notificationState.subject}
                label={t("Subject")}
                id="subject"
                onChange={e => setNotificationState(prev => ({ ...prev, subject: e.target.value }))}
                placeholder={t("Subject")}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField
                value={notificationState.content}
                label={t("Content")}
                id="content"
                onChange={e => setNotificationState(prev => ({ ...prev, content: e.target.value }))}
                placeholder={t("Message Contents")}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
    </Page>
  );
}

const notificationUserTypes = (t: TFunction<"translation", undefined>) => {
  return [
    {
      title: t("Admin"),
      normalized: "admin",
    },
    {
      title: t("Customer"),
      normalized: "customer",
    },
  ];
};
